import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Monitor, Clock, Target } from 'lucide-react';
import { ZONES } from '../data/content';
import SectionHead from './SectionHead';
import { Reveal, EASE } from '../lib/motion';

const Screen = ({ active }) => (
  <span className={`inline-flex h-3.5 w-5 items-center justify-center rounded-[3px] transition-colors duration-300 ${active ? 'bg-[#FF6B00] shadow-sm shadow-[#FF6B00]/50' : 'bg-[#3A3F4B]'}`}>
    <Monitor className="h-2 w-2 text-white" />
  </span>
);

export default function ZonesSection() {
  const [active, setActive] = useState('kassenzone');
  const zone = ZONES.find((z) => z.id === active);

  const block = (id, title, sub, screens, cls = '') => {
    const on = active === id;
    return (
      <button
        key={id}
        type="button"
        data-testid={`zone-block-${id}`}
        onClick={() => setActive(id)}
        aria-pressed={on}
        className={`group relative rounded-xl border p-4 text-left transition-[border-color,background-color,box-shadow] duration-300 ${cls} ${
          on ? 'border-[#FF6B00] bg-[#FF6B00]/10 shadow-lg shadow-[#FF6B00]/10' : 'border-[#282C37] bg-[#16181D] hover:border-[#7D8391]'
        }`}
      >
        <div className="flex flex-wrap items-center gap-1.5">
          {Array.from({ length: screens }).map((_, i) => <Screen key={`${id}-screen-${i}`} active={on} />)}
        </div>
        <p className="mt-3 font-display text-sm font-bold sm:text-base">{title}</p>
        <p className="mt-0.5 text-xs leading-snug text-[#7D8391]">{sub}</p>
      </button>
    );
  };

  return (
    <section id="zonen" data-testid="zones-section" className="chapter bg-[#16181D]/40">
      <div className="wrap">
        <SectionHead
          n="02"
          eyebrow="Der Markt"
          title="Fünf Zonen, fünf Situationen."
          testid="zones-headline"
          lead="Jeder Bildschirm hängt an einem Ort mit eigener Logik: Wo gewartet wird, wirken längere Botschaften. Wo genossen wird, wirkt Appetit. Sie wählen die Zonen, die zu Ihrem Angebot passen."
        />

        <div className="mt-16 grid gap-8 lg:grid-cols-12 lg:gap-12">
          <Reveal className="lg:col-span-7">
            <div className="rounded-2xl border border-[#282C37] bg-[#0F1012] p-4 sm:p-6" data-testid="zones-schematic">
              <div className="mb-5 flex items-center justify-between">
                <p className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-[#7D8391]">Erdgeschoss · 5.000 m² · schematisch</p>
                <p className="hidden items-center gap-2 font-mono-num text-[10px] uppercase tracking-widest text-[#7D8391] sm:flex"><Screen active /> Bildschirm</p>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {block('eingang', 'Eingang & Frische', 'Obst & Gemüse, Regionales', 2)}
                {block('bedientheken', 'Bedientheken', 'Fleisch, Wurst, Käse, Fisch, Wein', 3)}
                {block('mall', 'Mall & Vorkasse', 'Ladenstraße, Gastronomie', 2)}
                <div className="col-span-2 flex min-h-[110px] items-center justify-center rounded-xl border border-dashed border-[#282C37] p-4 sm:col-span-3" data-testid="zones-salesfloor">
                  <p className="font-display text-sm font-bold text-[#5B606C]">Verkaufsfläche · Regalgänge</p>
                </div>
                {block('kassenzone', 'Kassenzone', 'Alle kommen hier vorbei', 4, 'col-span-2')}
                {block('leergut', 'Leergut', 'Wartezone am Automaten', 1)}
              </div>
              <p className="mt-4 text-xs text-[#7D8391]">Position der Bildschirme beispielhaft – die genaue Platzierung zeigen wir Ihnen gern vor Ort.</p>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="flex flex-wrap gap-2" data-testid="zones-selector" role="tablist">
              {ZONES.map((z) => (
                <button
                  key={z.id}
                  type="button"
                  role="tab"
                  aria-selected={active === z.id}
                  data-testid={`zone-tab-${z.id}`}
                  onClick={() => setActive(z.id)}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition-[background-color,color,border-color] duration-200 ${
                    active === z.id ? 'bg-[#FF6B00] text-white' : 'border border-[#282C37] bg-[#16181D] text-[#B6BCC8] hover:border-[#7D8391] hover:text-white'
                  }`}
                >
                  {z.short}
                </button>
              ))}
            </div>
            <AnimatePresence mode="wait">
              <motion.div
                key={zone.id}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.35, ease: EASE }}
                className="mt-6 rounded-2xl border border-[#FF6B00]/40 bg-[#16181D] p-7 sm:p-9"
                data-testid="zone-detail"
              >
                <p className="eyebrow">Ausgewählte Zone</p>
                <h3 className="mt-3 font-display text-2xl font-extrabold tracking-tight sm:text-3xl" data-testid="zone-detail-name">{zone.name}</h3>
                <dl className="mt-5 grid grid-cols-2 gap-4 border-y border-[#282C37] py-4">
                  <div>
                    <dt className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-[#7D8391]"><Clock className="h-3.5 w-3.5" /> Aufenthalt</dt>
                    <dd className="mt-1 font-mono-num text-sm font-semibold text-[#FF6B00]" data-testid="zone-detail-dwell">{zone.dwell}</dd>
                  </div>
                  <div>
                    <dt className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-[#7D8391]"><Target className="h-3.5 w-3.5" /> Passt für</dt>
                    <dd className="mt-1 text-sm font-semibold text-white">{zone.fit}</dd>
                  </div>
                </dl>
                <p className="t-body mt-5" data-testid="zone-detail-desc">{zone.desc}</p>
              </motion.div>
            </AnimatePresence>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
